import React, { memo, useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal } from '@Sage/hoi-poi-ui';
import { CopilotContext } from '@Sage/ssm-web5';

import { EntityActions, EntityCrudActions, EntityDetailActions } from 'actions';
import MergeFilledIcon from 'components/SvgIcons/MergeFilled';
import TooltipHoi from 'components/TooltipHoi';
import SageCopilotIcon from 'lib/metadata/icons/SageCopilot';
import UsageLimitInfo from 'containers/components/UsageLimitInfo';
import { getActiveCrudName } from 'utils/crud';
import { getLiteral } from 'utils/getLiteral';
import { isEqual } from 'utils/objects';
import { ensureRoute } from 'utils/routes';

const EntityCrudHeader = memo(
    ({
        canMerge,
        cancelText,
        entity,
        hideSave,
        id,
        isReadOnly,
        loading,
        loadingSave,
        onCancel,
        onSave,
        saveText,
        title,
    }) => {
        const dispatch = useDispatch();

        // ========================================
        // CONTEXT
        // ========================================
        const { copilotBarProps, setCopilotBarProps } = useContext(CopilotContext);
        const { isMinimized: isCopilotBarMinimized, isOpen: isCopilotBarOpen } = copilotBarProps;

        // ========================================
        // REDUX STATE & SELECTORS
        // ========================================
        const activeCrudName = useSelector((state) => getActiveCrudName(state));

        const activeCrud = useSelector((state) => state.entityCrud?.[activeCrudName] || {});

        const { data = [], fieldsToClear = {}, duplicateNoChanges, originalData } = activeCrud;

        const isConfirmModalOpen = useSelector((state) => state.entityCrud.isConfirmModalOpen);
        const isExtraFieldsModalOpen = useSelector(
            (state) => state.entityCrud.isExtraFieldsModalOpen,
        );
        const urlToOpen = useSelector((state) => state.entityCrud.urlToOpen);
        const queryString = useSelector((state) => state.entityCrud.queryString);
        const nextDetailEntityType = useSelector(
            (state) => state.entityDetail.nextDetailEntityType,
        );
        const nextDetail = useSelector((state) => state.entityDetail.nextDetail);

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const salesOrderType = useMemo(() => data?.salesOrderTypeDescription, [data]);

        const isEmptyCrud = useMemo(() => {
            const hasEditedFields = data && Object.keys(data).some((key) => data[key]);
            const hasFieldsToClear = Object.keys(fieldsToClear).length > 0;
            return !hasEditedFields && !hasFieldsToClear;
        }, [data, fieldsToClear]);

        const disableSave = useMemo(
            () => isEmptyCrud || loadingSave || isReadOnly || duplicateNoChanges,
            [duplicateNoChanges, isEmptyCrud, isReadOnly, loadingSave],
        );

        const showMerge = useMemo(() => canMerge && id, [canMerge, id]);

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const handleSave = useCallback(() => {
            if (loadingSave) return Promise.resolve();
            return onSave()?.catch(console.error);
        }, [loadingSave, onSave]);

        const handleConfirm = useCallback(() => {
            dispatch(EntityDetailActions.resetNextDetail());
            onCancel();
            if (nextDetail) {
                const isMapOpen = window.location.href.includes('/map/');
                ensureRoute(
                    isMapOpen
                        ? `${nextDetailEntityType.entity}/map/${nextDetail.Id}`
                        : `/${nextDetailEntityType.entity}/${nextDetail.Id}`,
                );
            }
            dispatch(EntityCrudActions.toggleConfirmModal(false));
        }, [dispatch, nextDetail, nextDetailEntityType, onCancel]);

        const goToSettings = useCallback(() => {
            ensureRoute(urlToOpen, queryString);
        }, [queryString, urlToOpen]);

        const checkChanges = useCallback(() => {
            isEqual(originalData, data, true)
                ? handleConfirm()
                : dispatch(EntityCrudActions.toggleConfirmModal(true));
        }, [data, dispatch, handleConfirm, originalData]);

        const handleClose = useCallback(() => {
            dispatch(EntityDetailActions.resetNextDetail());
            dispatch(EntityCrudActions.toggleExtraFieldsModal(false));
            dispatch(EntityCrudActions.toggleConfirmModal(false));
        }, [dispatch]);

        const handleMerge = useCallback(() => {
            dispatch(EntityActions.openMergeEntityDialog(id));
        }, [dispatch, id]);

        const handleCopilotToggle = useCallback(() => {
            setCopilotBarProps({ isMinimized: !isCopilotBarMinimized });
        }, [isCopilotBarMinimized, setCopilotBarProps]);

        const handleModalConfirm = useCallback(() => {
            return isConfirmModalOpen ? handleConfirm() : goToSettings();
        }, [goToSettings, handleConfirm, isConfirmModalOpen]);

        // ========================================
        // MEMOIZED PROPS
        // ========================================
        const modalProps = useMemo(
            () => ({
                cancelText: getLiteral('action_cancel'),
                confirmText: getLiteral('action_discard_changes'),
                isOpen: isConfirmModalOpen || isExtraFieldsModalOpen,
                onCancel: handleClose,
                onConfirm: handleModalConfirm,
                overlayClassName: 'entity-crud-header__overlay-cancel',
                size: 'small',
                title: getLiteral('label_discard_unsaved_changes'),
                useCornerClose: false,
            }),
            [handleClose, handleModalConfirm, isConfirmModalOpen, isExtraFieldsModalOpen],
        );

        const cancelButton = useMemo(
            () => (
                <Button className="fm-button-header__cancel" onClick={checkChanges} type="terciary">
                    {cancelText}
                </Button>
            ),
            [cancelText, checkChanges],
        );

        const saveButton = useMemo(() => {
            if (hideSave) return null;

            return (
                <Button
                    className="fm-button-header__save"
                    isDisabled={disableSave}
                    onClick={handleSave}
                >
                    {saveText}
                </Button>
            );
        }, [disableSave, handleSave, hideSave, saveText]);

        // ========================================
        // RENDER
        // ========================================
        return (
            <div className="entity-crud-header">
                <div className="entity-crud-title">
                    {loading ? null : salesOrderType ? `${title} ${salesOrderType}` : title}
                    <UsageLimitInfo entity={entity} />
                </div>
                {isCopilotBarOpen && (
                    <Button
                        className="fm-entity-crud-header__copilot-bar-toggle"
                        customIcon={<SageCopilotIcon />}
                        isDisabled={!isCopilotBarMinimized}
                        onClick={handleCopilotToggle}
                        preserveCustomIconColors={isCopilotBarMinimized}
                        type="promotion"
                    >
                        {getLiteral('label_copilot')}
                    </Button>
                )}
                <div className="entity-crud-buttons">
                    {showMerge && (
                        <TooltipHoi placement="bottom" content={getLiteral('action_merge')}>
                            <div onClick={handleMerge} className="entity-crud-buttons__merge">
                                <MergeFilledIcon />
                            </div>
                        </TooltipHoi>
                    )}
                    {cancelButton}
                    {saveButton}
                </div>
                <Modal {...modalProps} />
            </div>
        );
    },
);

export default EntityCrudHeader;
